import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async"; // SEO support

const Hero = () => {
  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden bg-white text-gray-900">
      {/* ✅ SEO Meta Tags */}
      <Helmet>
        <title>Quadrangle Solutions | Shipping & Logistics Software</title>
        <meta
          name="description"
          content="Quadrangle Solutions offers innovative software for shipping, logistics, and beyond. With 15+ years of expertise, we deliver scalable, customized solutions."
        />
        <meta
          name="keywords"
          content="Quadrangle Solutions, shipping software, logistics software, freight forwarding, customs automation, AI logistics, scalable IT solutions"
        />
        <meta name="author" content="Quadrangle Solutions" />
        <meta property="og:title" content="Quadrangle Solutions" />
        <meta
          property="og:description"
          content="Innovating software for shipping, logistics, and beyond. Explore our solutions."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://quadranglesolutions.com/" />
        <meta
          property="og:image"
          content="https://quadranglesolutions.com/og-image.jpg"
        />
      </Helmet>

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#111827] via-transparent to-white opacity-40"></div>

      {/* Animated gradient blobs */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-green-500 opacity-20 animate-pulse"
            style={{
              width: `${120 + i * 100}px`,
              height: `${120 + i * 100}px`,
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animationDuration: `${15 + i * 5}s`,
              animationDelay: `${i * 2}s`,
            }}
          ></div>
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 md:py-40 text-center">
        <h1 className="text-5xl md:text-7xl font-bold mb-6 text-[#111827]">
          <span className="block">Welcome to</span>{" "}
          <span className="block mt-2 bg-clip-text text-transparent bg-gradient-to-r from-green-500 to-green-700">
            Quadrangle Solutions
          </span>
        </h1>

        <p className="mt-6 text-xl md:text-2xl max-w-3xl mx-auto text-gray-700 font-medium">
          Innovating Software for the Shipping, Logistics & Beyond powered by
          AI, ML, and Generative AI.
        </p>

        <p className="mt-6 text-lg md:text-xl max-w-3xl mx-auto text-gray-600 leading-relaxed">
          With over 15 years of domain expertise, Quadrangle Solutions is a
          trusted name in delivering robust, scalable, and customized software
          solutions, primarily serving the Shipping and Logistics industry.
        </p>

        {/* Buttons */}
        <div className="mt-10 flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-4">
          <Link
            to="/contact"
            className="px-8 py-4 border border-transparent text-lg font-semibold rounded-md text-white bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 shadow-lg transform transition-all duration-300 hover:scale-105"
          >
            Get in Touch
          </Link>
          <Link
            to="/services-product"
            className="px-8 py-4 border border-green-600 text-lg font-semibold rounded-md text-green-700 bg-green-50 hover:bg-green-100 shadow-lg transform transition-all duration-300 hover:scale-105"
          >
            Explore Our Services
          </Link>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
        <div className="animate-bounce">
          <svg
            className="w-8 h-8 text-green-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M19 14l-7 7m0 0l-7-7m7 7V3"
            ></path>
          </svg>
        </div>
      </div>

      {/* Animation keyframes */}
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        h1 {
          animation: fadeInUp 0.8s ease-out forwards;
        }
        p:nth-of-type(1) {
          animation: fadeInUp 0.8s ease-out 0.2s forwards;
          opacity: 0;
        }
        p:nth-of-type(2) {
          animation: fadeInUp 0.8s ease-out 0.4s forwards;
          opacity: 0;
        }
        .mt-10 {
          animation: fadeInUp 0.8s ease-out 0.6s forwards;
          opacity: 0;
        }
      `}</style>
    </div>
  );
};

export default Hero;
